import { ValidationError } from "@common/validationError";
import { isNullOrWhitespace } from "@common/stringOperations";
import { exhaustiveGuard } from "@common/utilities";
import { useTagsMultiSelectAutoUpdater } from "@common/components/tagsMultiSelect/tagsMultiSelectAutoUpdater/TagsMultiSelectAutoUpdater.hooks";
import { TagRelationType } from "@common/components/tagsMultiSelect/TagsMultiSelect.types";
import { useTranslation } from "@localization/useTranslation";
import { EntityTemplateField, EntityTemplateStep, isProcessStandardField, ProcessStandardField } from "../../entityTemplates/EntityTemplate.types";
import { ProcessFieldModel } from "../../entityTemplates/ProcessFieldRenderers";
import { hasFieldData } from "../../entityTemplates/fieldValue";
import { useResponsibleQuery } from "../../user/hooks/useResponsibleQuery";
import { useProcessDocuments } from "../processOverview/documents/ProcessDocument.hooks";

// Tags and documents are saved through their own endpoints rather than on the process, so their values
// come from the queries the fields themselves render from.
export interface ProcessRelatedValues {
    selectedTags: Array<string>;
    documentIds: Array<string>;
}

export function useValidateProcessStep(processId: string) {
    const { translateString } = useTranslation();
    const { selectedTags } = useTagsMultiSelectAutoUpdater(processId, TagRelationType.Process, false);
    const documents = useProcessDocuments(processId);
    const { userData } = useResponsibleQuery();

    const relatedValues: ProcessRelatedValues = {
        selectedTags,
        documentIds: documents.data?.map((x) => x.processDocumentId) ?? [],
    };

    function validateStep(step: EntityTemplateStep, process: ProcessFieldModel) {
        return step.fields
            .map((field) => validateValue(getProcessFieldValue(field, process, relatedValues), field, process))
            .filter((validationError): validationError is ValidationError => validationError !== null);
    }

    function validateValue(value: any, field: EntityTemplateField, process: ProcessFieldModel): ValidationError | null {
        const fieldName = getProcessFieldName(field);

        if (field.required && !hasFieldData(value)) {
            return new ValidationError(fieldName, translateString("requiredField"));
        }

        if (field.standardFieldIdentifier === ProcessStandardField.ProcessName && isNullOrWhitespace(value)) {
            return new ValidationError(fieldName, translateString("nameRequired"));
        }

        if (field.standardFieldIdentifier === ProcessStandardField.ProcessResponsible) {
            const user = userData?.find((x) => x.id === process.responsibleId);
            if (user && !user.active) {
                return new ValidationError(fieldName, translateString("userDeactivated"));
            }
        }

        return null;
    }

    return { validateStep, relatedValues };
}

export function getProcessFieldName(field: EntityTemplateField) {
    return field.standardFieldIdentifier ? ProcessStandardField[field.standardFieldIdentifier].toString() : field.id!;
}

export function getProcessFieldValue(field: EntityTemplateField, process: ProcessFieldModel, relatedValues: ProcessRelatedValues) {
    if (field.standardFieldIdentifier && isProcessStandardField(field.standardFieldIdentifier)) {
        switch (field.standardFieldIdentifier) {
            case ProcessStandardField.ProcessName:
                return process.name;
            case ProcessStandardField.ProcessDescription:
                return process.description;
            case ProcessStandardField.ProcessNote:
                return process.note;
            case ProcessStandardField.ProcessStatus:
                return process.status;
            case ProcessStandardField.ProcessBusinessAreas:
                return process.businessAreaIds;
            case ProcessStandardField.ProcessComplianceAreas:
                return process.complianceAreaIds;
            case ProcessStandardField.ProcessTags:
                return relatedValues.selectedTags;
            case ProcessStandardField.ProcessResponsible:
                return process.responsibleId;
            case ProcessStandardField.ProcessAdditionalUserAccess:
                return process.additionalAccessUsers;
            case ProcessStandardField.ProcessBusinessAreaAccess:
                return process.accessBusinessAreas;
            case ProcessStandardField.ProcessExcludedUsers:
                return process.excludedUsers;
            case ProcessStandardField.ProcessVisibleToAllUsers:
                return process.visibleToAllUsers;
            // A process open to all group companies has no rows of its own, so that choice alone fills the field in.
            case ProcessStandardField.ProcessGroupCompanies:
                return process.allGroupEntityAccess || hasFieldData(process.groupEntities);
            case ProcessStandardField.ProcessAssets:
                return process.assetIds;
            case ProcessStandardField.ProcessLegalEntities:
                return process.legalEntityIds;
            case ProcessStandardField.ProcessProcessingActivities:
                return process.processingActivityIds;
            case ProcessStandardField.ProcessDocuments:
                return relatedValues.documentIds;
            default:
                return exhaustiveGuard(field.standardFieldIdentifier);
        }
    }

    return process.customFieldValues?.find((x) => x.customInputFieldId === field.customInputFieldId)?.value;
}
